import { useState } from 'react';
import { Modal } from './Modal';
import { commissionerMessageApi } from '../services/api-client';

interface CommissionerMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  leagueId: string;
  recipientMemberId: string;
  recipientName: string;
}

export function CommissionerMessageModal({
  isOpen,
  onClose,
  leagueId,
  recipientMemberId,
  recipientName,
}: CommissionerMessageModalProps) {
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setContent('');
    setSent(false);
    setError(null);
    onClose();
  };

  const handleSend = async () => {
    if (!content.trim()) return;
    try {
      setSending(true);
      setError(null);
      await commissionerMessageApi.send(leagueId, recipientMemberId, content.trim());
      setSent(true);
      setContent('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Send Message">
      <div className="space-y-4">
        <div>
          <span className="text-sm text-gray-500">To:</span>{' '}
          <span className="text-sm font-semibold text-gray-900">{recipientName}</span>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
          <span className="font-semibold">Commissioner-only feature.</span> Only you can initiate
          direct messages — members cannot reply or send messages of their own.
        </div>

        {sent ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <div className="text-green-700 font-medium">Message sent!</div>
            <button
              onClick={handleClose}
              className="mt-3 px-4 py-2 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your message..."
              rows={5}
              maxLength={2000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-purple-500 focus:border-purple-500 resize-none"
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending || !content.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
